export type Sorter<T> = (a: T, b: T) => number;

/**
 * Returns a comparator on the callback result of two objects.
 * A more generic version of the {@see sortBy} function.
 * @typeParam T type of the items to be sorted
 * @typeParam O sortable
 * @param cb Callback to retrieve the sortable property from the items
 */
export const sortByCallback = <T, O = number>(cb: (x: T) => O) =>
    (a: T, b: T): number => {
        const va = cb(a);
        const vb = cb(b);
        return (va > vb ? 1 : 0) - (va < vb ? 1 : 0);
    };

/**
 * Returns a comparator on fields of two objects.
 * A special case of the {@see sortByCallback} function.
 * @typeParam T type of the items to be sorted
 * @typeParam K property of T
 * @param field The field to sort by. Should contain a comparable value
 */
export const sortBy = <T, K extends keyof T>(field: K): Sorter<T> => sortByCallback<T, T[K]>(x => x[field]);

export const sortByLocaleCompare = <T>(cb: (x: T) => string) =>
    (a: T, b: T): number => {
        const va = cb(a);
        const vb = cb(b);
        return va.localeCompare(vb);
    };

export const invertSorter = <T>(sorter: Sorter<T>): Sorter<T> =>
    (a, b) => sorter(b, a);
